import {
  EmploymentStatus,
  Gender,
  JobStatus,
  ListResponse,
  TeacherClassTimeStatistics,
} from '@/types/common';
import {
  TeacherClassRecord,
  TeacherDetail,
  TeacherInfo,
  TeacherClassHourSetting,
  TeacherUnsettledCourseList,
  TeacherSettledCourseDetail,
  TeacherSearchResponse,
} from '@/types/teacher';
import { requestWrapper } from '@/utils/request';
import { ScheduleMode } from '@/types/course';

// 创建老师需要的参数
export interface CreateTeacherParams {
  id?: number; // 老师 ID
  avatar: string; // 头像链接
  gender: Gender; // 性别
  status: EmploymentStatus; // 状态，例如 1 表示在职
  employment_type: JobStatus; // 就业类型，例如 "全职"
  store_id_arr: number[]; // 所属门店 ID 数组
  stage_name: string; // 艺名或教师名称
  real_name: string; // 真实姓名
  intro: string; // 个人介绍
  phone_number: string; // 手机号
  wx_number: string; // 微信号
  id_card?: string; // 身份证号
  other_tags: string[]; // 其他标签，例如 ["有难度", "晚上排课"]
  dance_styles: number[]; // 擅长的舞蹈风格，例如 ["JAZZ", "HIP-HOP"]
}

// 创建教师
export async function createTeacher(body: CreateTeacherParams) {
  return requestWrapper<TeacherInfo>('/api/teacher/create', {
    method: 'POST',
    data: body,
  });
}

// 更新教师信息
export async function updateTeacher(body: Partial<CreateTeacherParams>) {
  return requestWrapper<TeacherInfo>('/api/teacher/update', {
    method: 'POST',
    data: body,
  });
}

// 获取老师列表
export async function getTeacherList(params?: {
  page?: number;
  page_size?: number;
  status?: EmploymentStatus;
  stage_name?: string;
  phone_number?: string;
  dance_style_ids?: number[];
}) {
  const defaultParams = {
    page: 1,
    page_size: 20,
    status: EmploymentStatus.EMPLOYED,
  };
  const mergedParams = {
    ...defaultParams,
    ...params,
  };
  return requestWrapper<ListResponse<TeacherInfo>>('/api/teacher/list', {
    method: 'GET',
    params: mergedParams,
  });
}

export type TeacherSearchType = 'name' | 'phone';

// 通过姓名或手机号搜索老师
export async function searchTeacher(body: { search_type: TeacherSearchType; keyword: string }) {
  return requestWrapper<TeacherSearchResponse>('/api/teacher/search', {
    method: 'POST',
    data: body,
  });
}

// 获取老师详情
export async function getTeacherInfo(id: number) {
  return requestWrapper<TeacherDetail>('/api/teacher/info', {
    method: 'GET',
    params: { id },
  });
}

// 根据舞种查老师
export async function getTeacherListByDanceStyle(dance_style_ids: number[]) {
  return requestWrapper<ListResponse<TeacherInfo>>('/api/teacher/by_dance_style_id', {
    method: 'GET',
    params: { dance_style_ids },
  });
}
//获取老师排课记录
export async function getTeacherClassRecord(params: {
  teacher_id: number;
  course_type_ids?: string;
  status_str?: string;
  start_time?: string;
  end_time?: string;
  store_id?: number;
  page?: number;
  page_size?: number;
}) {
  return requestWrapper<ListResponse<TeacherClassRecord>>('/api/course_schedule/list_page', {
    method: 'GET',
    params,
  });
}

// 保存老师课时费
export async function saveTeacherClassHour(body: TeacherClassHourSetting) {
  return requestWrapper<void>('/api/teacher/fee_setting/save', {
    method: 'POST',
    data: body,
  });
}

// 删除老师课时费
export async function deleteTeacherClassHour(body: {
  teacher_id: number;
  course_type_id: number;
  course_schedule_type: ScheduleMode;
}) {
  return requestWrapper<void>('/api/teacher/fee_setting/delete', {
    method: 'POST',
    data: body,
  });
}

// 获取老师课时费列表
export async function getTeacherClassHourList(params: { teacher_id: number }) {
  return requestWrapper<ListResponse<TeacherClassHourSetting>>('/api/teacher/fee_setting/list', {
    method: 'GET',
    params,
  });
}

// 获取老师未结算课程列表
export async function getTeacherUnsettledCourseList(params: {
  store_id?: number;
  start_date: string;
  end_date: string;
  page: number;
  page_size: number;
}) {
  return requestWrapper<ListResponse<TeacherUnsettledCourseList>>(
    '/api/teacher/fee_setting/unsettled_list',
    {
      method: 'GET',
      params,
    },
  );
}

// 获取老师已结算课时费汇总
export async function getTeacherSettledClassTimeStatistics(params: {
  teacher_id?: number;
  store_id?: number;
  start_date: string;
  end_date: string;
  export?: number;
  page?: number;
  page_size?: number;
}) {
  return requestWrapper<TeacherClassTimeStatistics<TeacherClassRecord>>(
    '/api/teacher/fee_setting/settled_summary',
    {
      method: 'GET',
      params,
    },
  );
}

// 导出已结算课时费汇总
export async function exportTeacherSettledClassTimeStatistics(params: {
  start_date: string;
  end_date: string;
  store_id?: number;
}) {
  return requestWrapper<void>('/api/teacher/fee_setting/fee_summary/export', {
    method: 'GET',
    params,
  });
}

// 已结算课程明细表
export async function getTeacherSettledCourseDetailList(params: {
  teacher_id: number; // 老师ID
  store_id: number; // 门店ID
  start_date: string; // 开始日期
  end_date: string; // 结束日期
  page: number; // 页码
  page_size: number; // 每页条数
  course_type_id?: number; // 课程类型ID（可选）
  course_schedule_type?: number; // 排课类型（可选）0: 普通课程，1: 班级课程
}) {
  return requestWrapper<ListResponse<TeacherSettledCourseDetail>>(
    '/api/teacher/fee_setting/settled_list',
    {
      method: 'GET',
      params,
    },
  );
}
